<template>
  <div class="board-review">
    <!-- ══════════════ login gate ══════════════ -->
    <div v-if="!data" class="gate">
      <div class="gate-card">
        <div class="wordmark">P V I S</div>
        <div class="gate-title">Salary &amp; Sustainability Model</div>
        <div class="gate-sub">2027 BOARD DECISION · BOARD MEMBERS ONLY</div>
        <form @submit.prevent="unlock()">
          <label class="gate-label" for="gate-email">Email</label>
          <input
            id="gate-email" v-model="email" type="email" placeholder="you@example.com"
            autocomplete="email" required class="gate-input" :disabled="loading"
          />
          <label class="gate-label" for="gate-pw">Board password</label>
          <input
            id="gate-pw" v-model="password" type="password" placeholder="••••••••"
            autocomplete="current-password" required class="gate-input" :disabled="loading"
          />
          <button type="submit" class="gate-btn" :disabled="loading || !password || !email">
            {{ loading ? 'CHECKING…' : 'OPEN THE MODEL' }}
          </button>
        </form>
        <p v-if="authError" class="gate-error">{{ authError }}</p>
        <p class="gate-note">
          Confidential remuneration data. Access is logged (email, time, IP address and device details).
        </p>
      </div>
    </div>

    <!-- ══════════════ instrument ══════════════ -->
    <template v-else>
      <header class="topbar">
        <div class="topbar-id">
          <span class="wordmark">P V I S</span>
          <span class="topbar-title">Salary &amp; Sustainability Model</span>
          <span class="topbar-meta">2027 BOARD DECISION · VUV</span>
        </div>
        <div class="topbar-actions no-print">
          <button class="tb-btn tb-btn-solid" @click="resetToProposal">Proposed structure</button>
          <button class="tb-btn" @click="signOut">Sign out</button>
        </div>
      </header>

      <div class="disclaimer">
        Simplified model simulation — the results are representative, not exact.
      </div>

      <!-- verdict banner — color follows the status thresholds -->
      <section class="verdict" :class="`verdict-${verdictStatus.key}`">
        <div class="verdict-hero">
          <div class="verdict-label">STAFF COST AS % OF INCOME · 2027</div>
          <div class="hero-figure">{{ (y27.staffPct * 100).toFixed(1) }}%</div>
          <div class="verdict-status">
            ⚠ {{ verdictStatus.word.toUpperCase() }} · {{ ptsVsBenchmark }}
          </div>
        </div>
        <div class="verdict-body">
          <p class="verdict-line" v-html="verdictSentence"></p>
          <div class="verdict-stats">
            <div><span class="vs-label">SALARY BILL</span><span class="vs-val">{{ fmtM(y27.staffing) }}</span><span class="vs-sub">{{ staffingDelta }}</span></div>
            <div><span class="vs-label">TOTAL INCOME</span><span class="vs-val">{{ fmtM(y27.totalIncome) }}</span><span class="vs-sub">{{ incomeDelta }}</span></div>
            <div><span class="vs-label">OPERATING RESULT</span><span class="vs-val">{{ fmtM(y27.operatingResult) }}</span><span class="vs-sub">{{ resultPctOfIncome }} of income</span></div>
            <div><span class="vs-label">FEE RISE TO HOLD 2026'S RESULT</span><span class="vs-val">+{{ feeToHold2026.toFixed(2) }}%</span></div>
          </div>
        </div>
      </section>

      <!-- signal strip -->
      <section class="stat-strip">
        <StatTile
          label="Staff cost % of income"
          :value="(y27.staffPct * 100).toFixed(1) + '%'"
          :delta="ptsVsBenchmark.toLowerCase()"
          :status-key="verdictStatus.key"
          :spark="results.map((r) => r.staffPct)"
          :benchmark="benchmark"
        />
        <StatTile
          label="Operating result"
          :value="fmtM(y27.operatingResult)"
          :delta="`${resultPctOfIncome} of income · 2026: ${fmtM(y26.operatingResult)}`"
          :status-key="y27.operatingResult >= y26.operatingResult ? 'good' : (y27.operatingResult > 0 ? 'warning' : 'critical')"
          :spark="results.map((r) => r.operatingResult)"
          :benchmark="0"
        />
        <StatTile
          label="Staffing cost 2027"
          :value="fmtM(y27.staffing)"
          :delta="`${staffingDeltaPct >= 0 ? '+' : ''}${staffingDeltaPct.toFixed(1)}% vs 2026 budget`"
          :spark="results.map((r) => r.staffing)"
        />
        <StatTile
          label="Break-even fee increase"
          :value="`+${feeToHold2026.toFixed(2)}%`"
          delta="holds 2026's operating result"
          :status-key="feeToHold2026 <= 0.01 ? 'good' : 'warning'"
        />
        <StatTile
          label="Months of salary in cash"
          :value="y27.monthsCash.toFixed(1)"
          :delta="`2027 · minimum 3.0 · 2026: 1.5`"
          :status-key="y27.monthsCash >= 3 ? 'good' : (y27.monthsCash >= 2 ? 'warning' : 'critical')"
          :spark="results.map((r) => r.monthsCash)"
          :benchmark="3"
        />
        <StatTile
          :label="`Cumulative result 2027–${scenario.horizonYear}`"
          :value="fmtM(cumulativeResult)"
          delta="sum of operating results at these levers"
          :status-key="cumulativeResult >= 0 ? 'good' : 'critical'"
        />
      </section>

      <div class="layout">
        <!-- ═════ lever rail ═════ -->
        <aside class="rail no-print" :class="{ 'rail-closed': !railOpen }">
          <button class="rail-toggle" type="button" @click="railOpen = !railOpen" :aria-expanded="railOpen ? 'true' : 'false'">
            <span class="rail-toggle-title">Levers &amp; scenario</span>
            <span class="rail-toggle-chevron" aria-hidden="true">{{ railOpen ? '▾' : '▸' }}</span>
          </button>
          <div v-if="!railOpen" class="rail-summary" @click="railOpen = true">
            Salary +{{ globalSalaryPct.toFixed(1) }}% · fees +{{ scenario.feeIncreasePct.toFixed(2) }}%
            <template v-if="scenario.partnershipEnabled"> · partnership {{ fmtM(scenario.partnershipRevenue) }}</template>
            <template v-for="st in enabledSteps" :key="st.key"> · {{ st.label.toLowerCase() }} {{ st.year }}</template>
            <template v-if="enabledClassrooms.length"> · +{{ enabledClassrooms.length }} class{{ enabledClassrooms.length > 1 ? 'es' : '' }}</template>
            · inflation {{ scenario.costInflationPct.toFixed(1) }}%/yr — tap to adjust
          </div>
          <div v-show="railOpen" class="rail-body">
          <h2 class="rail-title">Levers</h2>
          <LeverSlider
            v-model="globalSalaryPct"
            label="Salary scale increase"
            :min="0" :max="30" :step="0.1"
            :marker="proposedAvgPct" marker-label="PROPOSED"
            note="average package increase vs current scales; drags all groups proportionally"
          />
          <LeverSlider
            v-model="scenario.feeIncreasePct"
            label="School fees increase"
            :min="0" :max="15" :step="0.25" :decimals="2"
            :marker="0" marker-label="PROPOSED"
          />
          <LeverSlider
            v-model="scenario.enrolmentPct"
            label="Enrolment change"
            :min="-10" :max="10" :step="0.5"
            :marker="0" marker-label="MODEL"
            note="tuition-proportional; staffing held constant"
          />
          <LeverSlider
            v-model="scenario.feeElasticity"
            label="Fee → enrolment elasticity"
            :min="0" :max="2" :step="0.1" unit=""
            note="% of enrolment lost per 1% fee increase (0 = fees are frictionless)"
          />
          <div class="lever-block">
            <label class="toggle-row">
              <input type="checkbox" v-model="scenario.partnershipEnabled" />
              <span>Partnership program revenue</span>
            </label>
            <div v-if="scenario.partnershipEnabled" class="partnership-amount">
              <input
                type="number" min="0" step="1"
                :value="scenario.partnershipRevenue / 1e6"
                @input="scenario.partnershipRevenue = Number($event.target.value) * 1e6"
              />
              <span>M VUV / year</span>
            </div>
            <div class="rail-note">Already included in the 2027 model (40M, 40 partner students). Untick to see the year without it.</div>
          </div>

          <h2 class="rail-title rail-title-2">Per group</h2>
          <LeverSlider
            v-for="g in leverGroupList" :key="g.group"
            :model-value="scenario.groupIncreasePct[g.group] || 0"
            @update:model-value="scenario.groupIncreasePct = { ...scenario.groupIncreasePct, [g.group]: $event }"
            :label="g.group" :min="0" :max="60" :step="0.5" compact
          />
          <div class="rail-note">% package increase vs a full year on current (2026) scales. Proposed: {{ proposedPerGroupSummary }}.</div>

          <h2 class="rail-title rail-title-2">Add classrooms</h2>
          <div class="lever-block">
            <div v-for="(c, ci) in scenario.classrooms" :key="c.key" class="class-row">
              <label class="toggle-row step-row">
                <input type="checkbox" v-model="c.enabled" />
                <span v-if="!c.removable">{{ c.label }}</span>
                <select v-else v-model="c.type" class="year-select type-select">
                  <option value="y78">Class · Y7–8 basis</option>
                  <option value="y12">Class · Y1–2 basis</option>
                </select>
              </label>
              <select v-model.number="c.year" class="year-select" :disabled="!c.enabled">
                <option v-for="yy in classroomYears" :key="yy" :value="yy">{{ yy }}</option>
              </select>
              <button
                v-if="c.removable" type="button" class="remove-btn" title="Remove this class"
                @click="scenario.classrooms.splice(ci, 1)"
              >×</button>
            </div>
            <button type="button" class="add-class-btn" @click="addClassroom">+ Add another class</button>
            <div class="rail-note">
              Priced at max cost — one CAT 1b teacher + one average TA ({{ fmtM(classCost) }}/yr) — with revenue at
              75% of room capacity on local fees (Y7–8: 24 × 731,641; Y1–2: 25 × 696,367 ≈ +17.5M/yr each).
              Net ≈ +9M per class. Grows with the fee and inflation levers.
            </div>
          </div>

          <h2 class="rail-title rail-title-2">Beyond 2027</h2>
          <div class="lever-block">
            <label v-for="st in scenario.futureSteps" :key="st.key" class="toggle-row step-row">
              <input type="checkbox" v-model="st.enabled" />
              <span>{{ st.label }} — +{{ st.pct }}% scale raise in {{ st.year }}</span>
            </label>
            <div class="rail-note">Possible future steps, not yet agreed — shown in the projection charts only; the
              band tables stay at the 2027 (step 1) rates.</div>
          </div>
          <LeverSlider v-model="scenario.costInflationPct" label="Cost inflation / yr" :min="0" :max="8" :step="0.1" />
          <LeverSlider v-model="scenario.feeIndexationPct" label="Fee indexation / yr" :min="0" :max="8" :step="0.1" />
          <div class="lever-block select-row-wrap">
            <label class="select-row">
              <span>Project to</span>
              <select v-model.number="scenario.horizonYear">
                <option v-for="yy in horizonOptions" :key="yy" :value="yy">{{ yy }}</option>
              </select>
            </label>
          </div>

          <h2 class="rail-title rail-title-2">Solve</h2>
          <button class="solve-btn" @click="applyFeeSolve">
            Fee increase that holds 2026's result
            <span class="solve-val">+{{ feeToHold2026.toFixed(2) }}%</span>
          </button>
          <button class="solve-btn" @click="applySalarySolve">
            Salary increase affordable at 2026's result
            <span class="solve-val">+{{ salaryAffordable.toFixed(1) }}%</span>
          </button>

          <h2 class="rail-title rail-title-2">Scenarios</h2>
          <div class="lever-block">
            <div class="scenario-save">
              <input v-model="scenarioName" type="text" placeholder="Name this scenario" maxlength="30" />
              <button type="button" class="save-btn" :disabled="!scenarioName.trim() || savedScenarios.length >= 3" @click="saveScenario">Save</button>
            </div>
            <div v-for="(sc, si) in savedScenarios" :key="sc.key" class="saved-row">
              <button type="button" class="saved-name" :title="'Load ' + sc.name" @click="applyScenario(sc)">{{ sc.name }}</button>
              <button type="button" class="remove-btn" title="Delete" @click="deleteScenario(si)">×</button>
            </div>
            <div class="rail-note">Up to 3 saved scenarios; they appear in the comparison panel. Click a name to load it.</div>
          </div>

          <h2 class="rail-title rail-title-2">Status thresholds</h2>
          <ul class="thresholds">
            <li><span class="chip chip-good"></span>Good — at or below the 70% ceiling</li>
            <li><span class="chip chip-warning"></span>Warning — 70% to 75%</li>
            <li><span class="chip chip-serious"></span>Serious — 75% to 80%</li>
            <li><span class="chip chip-critical"></span>Critical — above 80%</li>
          </ul>
          </div>
        </aside>

        <!-- ═════ main ═════ -->
        <main class="main">
          <CollapsePanel
            v-if="showSec('staffpct')" id="staffpct"
            title="Staff cost as a share of income, against the 65% benchmark and 70% ceiling"
            meta="Staffing expense ÷ total income · 2028+ projected by this tool"
          >
            <StaffPctChart :rows="results" :benchmark="benchmark" :ceiling="ceiling" :steps="enabledSteps" />
          </CollapsePanel>

          <CollapsePanel v-if="showSec('income')" id="income" title="Income, expenses and what is left over" meta="VUV, millions">
            <IncomeExpenseChart :rows="results" />
            <div class="sub-head">
              <b>Operating result</b>
              <span class="panel-meta">the surplus in the gap above, drawn from zero · hatched years are projections</span>
            </div>
            <SurplusBars :rows="results" />
          </CollapsePanel>

          <div class="panel-pair">
            <CollapsePanel v-if="showSec('groups')" id="groups" title="Where the money goes">
              <p class="panel-sub">2027 packages by staff group at the current levers, vs the same staff a full year on 2026 scales.</p>
              <GroupBars :rows="groupRows" />
            </CollapsePanel>

            <div>
              <CollapsePanel v-if="showSec('breakeven')" id="breakeven" title="What it would take to pay for it">
                <p class="panel-sub">Fee increase on 2027 tuition; no increase is proposed. The red fill is the fee lever's current setting.</p>
                <BreakEvenMeter :current="scenario.feeIncreasePct" :markers="feeMarkers" :max-pts="15" />
                <p class="panel-note">
                  The alternative is the salary bill itself: holding staff cost at the 65% benchmark on 2027 income
                  means a bill of <b>{{ fmtM(at65.bill) }}</b> —
                  <b>{{ fmtM(at65.gap) }}, or {{ ((at65.gap / y27.staffing) * 100).toFixed(1) }}%, below the current setting</b>.
                </p>
              </CollapsePanel>

              <CollapsePanel v-if="showSec('tornado')" id="tornado" title="Which lever moves the result" focus>
                <p class="panel-sub">Change in the 2027 operating result for a 1-point move in each lever; the partnership program at its set amount.</p>
                <TornadoChart :items="sensitivity" :center-value="y27.operatingResult" />
                <p class="panel-note">
                  Fees and enrolment have the largest per-point effect on the result. A fee increase may itself
                  affect enrolment; the enrolment row can be used to test that interaction.
                </p>
              </CollapsePanel>
            </div>
          </div>

          <CollapsePanel v-if="showSec('fees')" id="fees" title="Fee position" meta="Annual tuition in AUD, regional comparators">
            <p class="panel-sub">
              Where PVIS fees sit against regional international schools and Australian independent schools.
              The PVIS bars follow the fee lever.
            </p>
            <FeeBenchmark :fee-increase-pct="scenario.feeIncreasePct" />
          </CollapsePanel>

          <CollapsePanel
            v-if="showSec('bands')" id="bands" title="Proposed 2027 salary bands"
            meta='Annual VUV · "vs 2026" compares each band with the average current package of the staff moving onto it'
          >
            <BandTable :staff-bands="data.bands.staff2027" :leadership-bands="data.bands.leadership2027" />
            <p class="table-foot">
              Gratuity and severance accrue at 8.33% of base and are included in the total package. VNPF is 6% employer
              and 6% employee; only the employer share is a cost to the school. The 2027 structure replaces the step
              scales used through 2026 — teachers move from T-steps to CAT 1a/1b, assistants and ancillary staff from
              TA/A steps to Bands 1–3.
            </p>
          </CollapsePanel>

          <CollapsePanel
            v-if="savedScenarios.length && showSec('scenarios')" id="scenarios" title="Scenario comparison"
            meta="Saved lever settings side by side, identical scales" focus
          >
            <ScenarioCompare :scenarios="compareScenarios" />
          </CollapsePanel>

          <div class="panel-pair">
            <CollapsePanel
              v-if="showSec('parity')" id="parity" title="Teacher pay vs Australian parity"
              meta="CAT 1b cash salary vs NSW top-of-scale classroom teacher"
            >
              <p class="panel-sub">
                Where the top classroom band sits against the Australian top of scale, on a gross or an
                after-tax basis — Vanuatu levies no personal income tax, which narrows the gap on the net view.
                Markers show the effect of the possible future scale steps.
              </p>
              <ParityGap :data="data" />
            </CollapsePanel>
            <CollapsePanel v-if="showSec('ratios')" id="ratios" title="Students per staff member" meta="2027 model vs the model's own targets">
              <p class="panel-sub">
                Staffing richness per student — the other side of the affordability question. The red dot is the
                2027 model; the black line is the target.
              </p>
              <RatiosPanel
                :ratios="data.ratios"
                :classrooms="scenario.classrooms"
                :enrolment-pct="scenario.enrolmentPct"
              />
            </CollapsePanel>
          </div>

          <div class="panel-pair">
            <CollapsePanel
              v-if="showSec('principal')" id="principal" title="Principal pay — premium and teacher parity"
              :meta="`Premium over CAT 1b (${fmtM(teacherTopPkg)}) · guideline +50–65%`"
            >
              <p class="panel-sub">
                The premium is measured against the highest-paid classroom band. Today it is
                +{{ ((principalBandPkg / teacherTopPkg - 1) * 100).toFixed(0) }}%, above the +50–65% guideline.
                The line shows the same premium if teacher pay converges to the Australian-parity assumption below
                while the Principal's package is unchanged; both the premium and the guideline comparison depend on
                where the teacher base sits.
              </p>
              <PremiumTrajectory
                :teacher-pkg2027="teacherTopPkg"
                :pkg="principalBandPkg"
              />
            </CollapsePanel>
            <CollapsePanel
              v-if="showSec('leadershipavg')" id="leadershipavg" title="Leadership team average — same measure"
              :meta="`Average of the six Head / Director band rates (${fmtM(avgLeadershipPkg)})`"
            >
              <p class="panel-sub">
                The average of the Heads of School and Directors (Principal excluded) is
                +{{ ((avgLeadershipPkg / teacherTopPkg - 1) * 100).toFixed(0) }}% over CAT 1b, against a weighted
                guideline of +32–42%. Under the parity assumption shown below, the average package would sit below
                the top classroom band. The AUD range below is drawn from Australian deputy-principal pay scales and
                school business-manager market data (2025, indicative).
              </p>
              <PremiumTrajectory
                :teacher-pkg2027="teacherTopPkg"
                :pkg="avgLeadershipPkg"
                role-label="average leadership"
                :lo="32" :hi="42"
                :au-lo="135000" :au-hi="175000"
                au-label="Australian deputy principal / school business manager"
              />
            </CollapsePanel>
          </div>

          <section class="footer-cols">
            <div>
              <h3 class="foot-title">Risks on the table</h3>
              <p><b>Margin compression.</b> {{ fmtM(y27.operatingResult) }} on {{ fmtM(y27.totalIncome) }} of income leaves
                little buffer for an enrolment dip or a maintenance surprise.</p>
              <p><b>A structural commitment.</b> {{ (y27.staffPct * 100).toFixed(1) }}% is a recurring level, not a one-year
                cost. Reversing it later means holding salaries flat while fees rise.</p>
              <p><b>Fee dependence.</b> Closing the gap with fees assumes families pay. Raising fees may itself cost
                enrolment — test that link with the enrolment sensitivity above.</p>
            </div>
            <div>
              <h3 class="foot-title">Notes on the data</h3>
              <p v-for="(n, i) in data.meta.notes" :key="i">{{ n }}</p>
              <p>2028+ is projected by this tool: costs grow at the inflation lever, income at the indexation lever;
                interest and depreciation follow the model's loan and asset schedules.</p>
            </div>
            <div>
              <h3 class="foot-title">Sources</h3>
              <p>{{ data.meta.source }} — <i>Summary Results, Profit and Loss, Staffing costs, Scenarios,
                staff/leadership remuneration and Staff Master Sheets 2026–2027</i>.</p>
              <p>Derived values — break-even points, sensitivities, group shares — are computed from those tabs and
                verified by a golden test that reproduces the workbook's 2027 figures.</p>
            </div>
          </section>

          <div class="bottom-bar">
            <span>BOARD REVIEW · CONFIDENTIAL</span>
            <span>CURRENCY VUV · FIGURES AS AT THE 03.08.26 WORKBOOK</span>
          </div>
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import LeverSlider from '../components/board/LeverSlider.vue';
import StatTile from '../components/board/StatTile.vue';
import StaffPctChart from '../components/board/StaffPctChart.vue';
import IncomeExpenseChart from '../components/board/IncomeExpenseChart.vue';
import SurplusBars from '../components/board/SurplusBars.vue';
import TornadoChart from '../components/board/TornadoChart.vue';
import GroupBars from '../components/board/GroupBars.vue';
import BandTable from '../components/board/BandTable.vue';
import BreakEvenMeter from '../components/board/BreakEvenMeter.vue';
import PremiumTrajectory from '../components/board/PremiumTrajectory.vue';
import CollapsePanel from '../components/board/CollapsePanel.vue';
import ParityGap from '../components/board/ParityGap.vue';
import RatiosPanel from '../components/board/RatiosPanel.vue';
import ScenarioCompare from '../components/board/ScenarioCompare.vue';
import FeeBenchmark from '../components/board/FeeBenchmark.vue';
import {
  defaultScenario,
  project,
  yearResult,
  classroomCost2027,
  leverGroups,
  proposedGroupPct,
  averageGroupPct,
  scenarioWithAveragePct,
  feeNeededFor,
  feeNeededForMargin,
  feeNeededForStaffPct,
  salaryAffordableFor,
  staffingAtPct,
  leverSensitivity,
  staffPctStatus,
  formatM,
} from '../utils/boardEngine';

const AUTH_KEY = 'pvisBoardReviewAuth';

export default {
  name: 'BoardSalaryReview',
  components: {
    LeverSlider, StatTile, StaffPctChart, IncomeExpenseChart,
    SurplusBars, TornadoChart, GroupBars, BandTable, BreakEvenMeter, PremiumTrajectory,
    CollapsePanel, ParityGap, RatiosPanel, ScenarioCompare, FeeBenchmark,
  },
  data() {
    return {
      email: '',
      password: '',
      authError: '',
      loading: false,
      data: null,
      scenario: null,
      railOpen: true,
      scenarioName: '',
      savedScenarios: [],
      adminSections: {},
    };
  },
  computed: {
    benchmark() { return this.data.meta.benchmarkSalaryPctOfIncome; },
    ceiling() { return 0.70; },
    results() { return project(this.data, this.scenario); },
    y26() { return yearResult(this.results, 2026); },
    y27() { return yearResult(this.results, 2027); },
    verdictStatus() { return staffPctStatus(this.y27.staffPct); },
    leverGroupList() { return leverGroups(this.data); },
    proposedPct() { return proposedGroupPct(this.data); },
    proposedAvgPct() { return averageGroupPct(this.data, this.proposedPct); },
    globalSalaryPct: {
      get() { return averageGroupPct(this.data, this.scenario.groupIncreasePct); },
      set(v) { this.scenario = scenarioWithAveragePct(this.data, this.scenario, v); },
    },
    proposedPerGroupSummary() {
      return this.leverGroupList
        .map((g) => `${g.group.split(' ')[0]} +${this.proposedPct[g.group].toFixed(1)}%`)
        .join(' · ');
    },
    ptsVsBenchmark() {
      const d = (this.y27.staffPct - this.benchmark) * 100;
      return `${d >= 0 ? '+' : ''}${d.toFixed(1)} PTS VS 65% BENCHMARK`;
    },
    staffingDeltaPct() { return (this.y27.staffing / this.y26.staffing - 1) * 100; },
    staffingDelta() {
      const d = this.y27.staffing - this.y26.staffing;
      return `${d >= 0 ? '+' : '−'}${formatM(Math.abs(d))} vs 2026`;
    },
    incomeDelta() {
      const d = (this.y27.totalIncome / this.y26.totalIncome - 1) * 100;
      return `${d >= 0 ? '+' : ''}${d.toFixed(1)}%`;
    },
    resultPctOfIncome() {
      return `${((this.y27.operatingResult / this.y27.totalIncome) * 100).toFixed(1)}%`;
    },
    feeToHold2026() {
      return Math.max(0, feeNeededFor(this.data, this.scenario, this.y26.operatingResult));
    },
    salaryAffordable() {
      return salaryAffordableFor(this.data, this.scenario, this.y26.operatingResult);
    },
    sensitivity() { return leverSensitivity(this.data, this.scenario); },
    at65() { return staffingAtPct(this.data, this.scenario, 0.65); },
    cumulativeResult() {
      return this.results.filter((r) => r.year >= 2027).reduce((s, r) => s + r.operatingResult, 0);
    },
    compareScenarios() {
      const saved = this.savedScenarios.map((sc) => ({
        key: sc.key,
        name: sc.name,
        results: project(this.data, sc.scenario),
      }));
      return [{ key: 'current', name: 'Current levers', results: this.results, current: true }, ...saved];
    },
    enabledSteps() {
      return (this.scenario.futureSteps || []).filter((st) => st.enabled && st.year <= this.scenario.horizonYear);
    },
    classCost() { return classroomCost2027(this.data); },
    classroomYears() {
      const last = this.data.financials.outYears[this.data.financials.outYears.length - 1];
      const opts = [];
      for (let yy = 2028; yy <= last; yy++) opts.push(yy);
      return opts;
    },
    enabledClassrooms() {
      return (this.scenario.classrooms || []).filter((c) => c.enabled);
    },
    teacherTopPkg() {
      return Math.max(
        ...this.data.bands.staff2027.filter((b) => /^teacher$/i.test(b.position.trim())).map((b) => b.package),
      );
    },
    principalBandPkg() {
      const p = this.data.bands.leadership2027.find((b) => /^principal/i.test(b.position));
      return p ? p.package : 0;
    },
    avgLeadershipPkg() {
      const roles = this.data.bands.leadership2027.filter((b) => /^(director|head of)/i.test(b.position));
      return roles.reduce((s, b) => s + b.package, 0) / (roles.length || 1);
    },
    feeMarkers() {
      const hold = feeNeededFor(this.data, this.scenario, this.y26.operatingResult);
      const kpi = feeNeededForMargin(this.data, this.scenario, 5);
      const bench = feeNeededForStaffPct(this.data, this.scenario, 0.65);
      return [
        { key: 'hold', value: Math.max(0, hold), label: `holds 2026's ${formatM(this.y26.operatingResult)} result`, hot: false, lane: 1 },
        { key: 'kpi', value: kpi, label: 'meets the 5% surplus KPI', hot: false, lane: 0 },
        { key: 'bench', value: bench, label: 'returns staff cost to 65%', hot: true, lane: 1 },
      ];
    },
    groupRows() {
      const pool = this.groupRowsRaw.reduce((s, g) => s + g.cost, 0);
      return this.groupRowsRaw.map((g) => ({ ...g, share: `${((g.cost / pool) * 100).toFixed(1)}%` }));
    },
    groupRowsRaw() {
      const rows = this.data.groups.map((g) => {
        const pct = this.scenario.groupIncreasePct[g.group] || 0;
        const unmatchedExtra = g.package2027 - g.package2027Matched;
        const cost = g.matched > 0 ? g.package2026 * (1 + pct / 100) + unmatchedExtra : g.package2027;
        return {
          group: g.group,
          headcount: g.headcount,
          fte: g.fte,
          cost,
          base2026: g.package2026,
          deltaPct: g.matched > 0 ? pct : 0,
          note: g.matched < g.headcount ? `${g.headcount - g.matched} new role outside the comparison` : '',
        };
      });
      return rows.sort((a, b) => b.cost - a.cost);
    },
    horizonOptions() {
      const last = this.data.financials.outYears[this.data.financials.outYears.length - 1];
      const opts = [];
      for (let yy = 2028; yy <= last; yy++) opts.push(yy);
      return opts;
    },
    verdictSentence() {
      const avg = this.globalSalaryPct;
      const fees = this.scenario.feeIncreasePct > 0
        ? `a <b>${this.scenario.feeIncreasePct.toFixed(2)}% fee increase</b>`
        : '<b>fees unchanged</b>';
      const partner = this.scenario.partnershipEnabled
        ? ` (partnership program included at ${formatM(this.scenario.partnershipRevenue)})`
        : ' with the partnership program removed';
      const res = this.y27.operatingResult;
      const resTxt = Math.abs(res) < 2e6
        ? `the operating result is <b>${formatM(res)}</b> — effectively break-even`
        : res > 0
          ? `the operating result is a surplus of <b>${formatM(res)}</b>`
          : `the operating result is a <b>deficit of ${formatM(res)}</b>`;
      const pos = this.y27.staffPct > this.ceiling
        ? 'above the <b>70% ceiling</b>'
        : this.y27.staffPct > this.benchmark
          ? 'above the <b>65% benchmark</b> but under the 70% ceiling'
          : 'within the <b>65% benchmark</b>';
      return `At a <b>+${avg.toFixed(1)}% average package increase</b> with ${fees}${partner}, staff cost reaches <b>${(this.y27.staffPct * 100).toFixed(1)}% of income</b> in 2027 — ${pos} — and ${resTxt}.`;
    },
  },
  mounted() {
    // Content first on small screens; the lever section starts collapsed there.
    if (window.innerWidth < 960) this.railOpen = false;
    try {
      this.savedScenarios = JSON.parse(sessionStorage.getItem('pvisBoardScenarios') || '[]');
    } catch (e) { /* ignore */ }
    try {
      const saved = JSON.parse(sessionStorage.getItem(AUTH_KEY) || 'null');
      if (saved && saved.email && saved.password) {
        this.email = saved.email;
        this.password = saved.password;
        this.unlock(true);
      }
    } catch (e) { /* ignore */ }
  },
  methods: {
    fmtM: formatM,
    // Admin-controlled visibility (access page); absent = visible.
    showSec(id) {
      return this.adminSections[id] !== false;
    },
    clientInfo() {
      let tz = '';
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { /* ignore */ }
      return {
        platform: (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '',
        language: navigator.language || '',
        timezone: tz,
        screen: `${window.screen.width}x${window.screen.height}`,
      };
    },
    async unlock(silent = false) {
      this.loading = true;
      this.authError = '';
      try {
        const res = await axios.post('/api/boardReview', {
          email: this.email.trim(),
          password: this.password,
          client: this.clientInfo(),
        });
        this.data = res.data.data;
        this.adminSections = res.data.sections || {};
        this.scenario = defaultScenario(this.data);
        sessionStorage.setItem(AUTH_KEY, JSON.stringify({ email: this.email.trim(), password: this.password }));
      } catch (e) {
        if (!silent) {
          if (e.response && e.response.status === 401) this.authError = 'Incorrect password.';
          else if (e.response && e.response.status === 400) this.authError = e.response.data.error;
          else this.authError = 'Could not reach the server. Please try again.';
        }
        sessionStorage.removeItem(AUTH_KEY);
      } finally {
        this.loading = false;
      }
    },
    signOut() {
      sessionStorage.removeItem(AUTH_KEY);
      this.data = null;
      this.scenario = null;
      this.password = '';
    },
    resetToProposal() {
      this.scenario = defaultScenario(this.data);
    },
    saveScenario() {
      const name = this.scenarioName.trim();
      if (!name || this.savedScenarios.length >= 3) return;
      this.savedScenarios.push({
        key: `sc-${name}-${this.savedScenarios.length}`,
        name,
        scenario: JSON.parse(JSON.stringify(this.scenario)),
      });
      this.scenarioName = '';
      sessionStorage.setItem('pvisBoardScenarios', JSON.stringify(this.savedScenarios));
    },
    applyScenario(sc) {
      this.scenario = JSON.parse(JSON.stringify(sc.scenario));
    },
    deleteScenario(si) {
      this.savedScenarios.splice(si, 1);
      sessionStorage.setItem('pvisBoardScenarios', JSON.stringify(this.savedScenarios));
    },
    addClassroom() {
      this.classCounter = (this.classCounter || 0) + 1;
      this.scenario.classrooms.push({
        key: `extra-${this.classCounter}`,
        label: 'Additional class',
        type: 'y78',
        enabled: true,
        year: 2028,
        removable: true,
      });
    },
    applyFeeSolve() {
      this.scenario.feeIncreasePct = Math.round(this.feeToHold2026 * 4) / 4;
    },
    applySalarySolve() {
      this.scenario = scenarioWithAveragePct(this.data, this.scenario, Math.round(this.salaryAffordable * 10) / 10);
    },
  },
};
</script>

<style scoped>
/* ── design tokens (designer's Swiss language) ─────────── */
.board-review {
  --paper: #ffffff;
  --page: #ffffff;
  --ink: #201e1d;
  --muted: #6f6b66;
  --hairline: #e3e1dc;
  --track: #eeece7;
  --hatch: #efede8;
  --red: #ec3013;
  --pink: #f6b3a4;
  --status-good: #159615;
  --status-warning: #f0a800;
  --status-serious: #ef7c4d;
  --status-critical: #ec3013;

  min-height: 100vh;
  background: var(--page);
  color: var(--ink);
  font-family: 'Helvetica Neue', Helvetica, Arial, system-ui, sans-serif;
  text-align: left; /* the site scaffold centers #app; this page is left-set */
}
.wordmark { font-weight: 800; letter-spacing: 0.28em; font-size: 15px; }

/* ── login gate ────────────────────────────────────────── */
.gate { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: #faf9f5; }
.gate-card {
  background: var(--paper); border: 1.5px solid var(--ink);
  padding: 40px 36px; max-width: 400px; width: 100%;
}
.gate-title { font-size: 21px; font-weight: 800; margin-top: 14px; letter-spacing: -0.01em; }
.gate-sub { font-size: 10px; letter-spacing: 0.12em; color: var(--muted); margin: 6px 0 26px; }
.gate-label { display: block; font-size: 10px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); margin: 14px 0 4px; }
.gate-input {
  width: 100%; box-sizing: border-box; padding: 10px 12px; font-size: 14px;
  border: 1px solid var(--ink); border-radius: 0; background: var(--paper); color: var(--ink);
}
.gate-input:focus { outline: 2px solid var(--red); outline-offset: -1px; }
.gate-btn {
  width: 100%; margin-top: 22px; padding: 12px; font-size: 12px; font-weight: 700; letter-spacing: 0.1em;
  border: none; border-radius: 0; background: var(--red); color: #fff; cursor: pointer;
}
.gate-btn:disabled { opacity: 0.5; cursor: default; }
.gate-error { color: var(--red); font-size: 13px; margin-top: 14px; font-weight: 600; }
.gate-note { color: var(--muted); font-size: 10.5px; margin-top: 22px; line-height: 1.5; }

/* ── header ────────────────────────────────────────────── */
.topbar {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  padding: 12px 24px; border-bottom: 1.5px solid var(--ink); flex-wrap: wrap;
}
.topbar-id { display: flex; align-items: baseline; gap: 16px; flex-wrap: wrap; }
.topbar-title { font-size: 15px; font-weight: 700; }
.topbar-meta { font-size: 10px; letter-spacing: 0.12em; color: var(--muted); }
.topbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.tb-btn {
  background: none; border: 1px solid var(--ink); color: var(--ink); border-radius: 0;
  padding: 6px 12px; font-size: 11px; font-weight: 600; letter-spacing: 0.04em; cursor: pointer;
}
.tb-btn:hover { background: var(--track); }
.tb-btn-solid { background: var(--ink); color: #fff; }
.tb-btn-solid:hover { background: #000; }

/* ── verdict banner ────────────────────────────────────── */
.verdict {
  background: var(--red); color: #fff; padding: 26px 24px 22px;
  display: flex; gap: 40px; align-items: flex-start; flex-wrap: wrap;
}
/* status-driven banner tones, deep enough for white text */
.verdict-good { background: #157a15; }
.verdict-warning { background: #b97b00; }
.verdict-serious { background: #d95926; }
.verdict-critical { background: var(--red); }
.verdict-hero { min-width: 230px; }
.verdict-label { font-size: 10px; font-weight: 600; letter-spacing: 0.14em; opacity: 0.9; }
.hero-figure { font-size: 64px; font-weight: 800; line-height: 1.02; letter-spacing: -0.03em; margin: 4px 0; }
.verdict-status { font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; }
.verdict-body { flex: 1; min-width: 280px; }
.verdict-line { font-size: 18px; line-height: 1.5; margin: 0 0 18px; max-width: 760px; }
.verdict-line :deep(b) { font-weight: 800; }
.verdict-stats { display: flex; gap: 34px; flex-wrap: wrap; }
.verdict-stats > div { display: flex; flex-direction: column; }
.vs-label { font-size: 9px; font-weight: 600; letter-spacing: 0.12em; opacity: 0.85; margin-bottom: 4px; }
.vs-val { font-size: 20px; font-weight: 800; line-height: 1.1; }
.vs-sub { font-size: 10.5px; opacity: 0.85; margin-top: 2px; }

/* ── stat strip ────────────────────────────────────────── */
.stat-strip {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  border-bottom: 1.5px solid var(--ink);
}
.stat-strip > * { border-right: 1px solid var(--hairline); }
.stat-strip > *:last-child { border-right: none; }

/* ── layout ────────────────────────────────────────────── */
.layout { display: flex; align-items: flex-start; }
.rail {
  position: sticky; top: 0; width: 236px; flex-shrink: 0;
  border-right: 1.5px solid var(--ink);
  background: #f7f5f0;
  padding: 0 0 30px; max-height: 100vh; overflow-y: auto;
}
.rail-closed { padding-bottom: 0; }
.rail-toggle {
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  width: 100%; padding: 12px 18px; cursor: pointer;
  background: var(--ink); color: #fff; border: none; border-radius: 0;
  font-size: 11px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase;
  position: sticky; top: 0; z-index: 2;
}
.rail-toggle:hover { background: #000; }
.rail-toggle-chevron { font-size: 12px; }
.rail-summary {
  padding: 12px 18px; font-size: 11.5px; line-height: 1.6; color: var(--ink);
  cursor: pointer; font-variant-numeric: tabular-nums;
}
.rail-summary:hover { background: var(--track); }
.rail-body { padding: 16px 18px 0; }
.rail-title {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em;
  color: var(--ink); margin: 0 0 14px; padding-bottom: 6px; border-bottom: 1px solid var(--ink);
}
.rail-title-2 { margin-top: 26px; }
.rail-note { font-size: 10px; color: var(--muted); line-height: 1.5; margin-top: 6px; }
.lever-block { margin-bottom: 14px; }
.toggle-row { display: flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 600; color: var(--ink); cursor: pointer; }
.toggle-row input { accent-color: var(--red); width: 14px; height: 14px; }
.step-row { margin-bottom: 8px; font-weight: 400; }
.class-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.class-row .step-row { margin-bottom: 6px; flex: 1; min-width: 0; }
.year-select {
  padding: 2px 4px; font-size: 12px; border: 1px solid var(--ink); border-radius: 0;
  background: var(--paper); color: var(--ink); font-variant-numeric: tabular-nums;
}
.year-select:disabled { opacity: 0.4; border-color: var(--muted); }
.type-select { max-width: 128px; }
.remove-btn {
  background: none; border: 1px solid var(--muted); color: var(--muted); border-radius: 0;
  width: 20px; height: 20px; line-height: 1; padding: 0; cursor: pointer; font-size: 13px; flex-shrink: 0;
}
.remove-btn:hover { border-color: var(--red); color: var(--red); }
.add-class-btn {
  margin-top: 4px; background: none; border: 1px solid var(--ink); border-radius: 0;
  padding: 5px 10px; font-size: 11px; font-weight: 600; color: var(--ink); cursor: pointer;
}
.add-class-btn:hover { background: var(--track); }
.scenario-save { display: flex; gap: 6px; margin-bottom: 8px; }
.scenario-save input {
  flex: 1; min-width: 0; padding: 5px 8px; font-size: 12px; border: 1px solid var(--ink);
  border-radius: 0; background: var(--paper); color: var(--ink);
}
.save-btn {
  background: var(--ink); color: #fff; border: none; border-radius: 0;
  padding: 5px 12px; font-size: 11px; font-weight: 700; cursor: pointer;
}
.save-btn:disabled { opacity: 0.4; cursor: default; }
.saved-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
.saved-name {
  flex: 1; min-width: 0; text-align: left; background: none; border: 1px solid var(--hairline);
  border-radius: 0; padding: 5px 8px; font-size: 12px; color: var(--ink); cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.saved-name:hover { border-color: var(--ink); background: var(--track); }
.disclaimer {
  padding: 7px 24px; font-size: 11px; letter-spacing: 0.04em; color: var(--muted);
  border-bottom: 1px solid var(--hairline); background: #f7f5f0;
}
.partnership-amount { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 10px; color: var(--muted); }
.partnership-amount input {
  width: 60px; padding: 4px 6px; font-size: 13px; border: 1px solid var(--ink); border-radius: 0;
  background: var(--paper); color: var(--ink); font-variant-numeric: tabular-nums;
}
.select-row { display: flex; align-items: center; justify-content: space-between; font-size: 12.5px; font-weight: 600; }
.select-row select {
  padding: 4px 8px; font-size: 13px; border: 1px solid var(--ink); border-radius: 0;
  background: var(--paper); color: var(--ink);
}
.solve-btn {
  display: block; width: 100%; text-align: left; margin-bottom: 8px; padding: 9px 12px;
  font-size: 11px; color: var(--ink); background: none; cursor: pointer;
  border: 1px solid var(--ink); border-radius: 0; line-height: 1.45;
}
.solve-btn:hover { background: var(--track); }
.solve-val { display: block; font-size: 16px; font-weight: 800; font-variant-numeric: tabular-nums; }
.thresholds { list-style: none; padding: 0; margin: 0; }
.thresholds li { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--ink); margin-bottom: 7px; }
.chip { width: 9px; height: 9px; flex-shrink: 0; }
.chip-good { background: var(--status-good); }
.chip-warning { background: var(--status-warning); }
.chip-serious { background: var(--status-serious); }
.chip-critical { background: var(--status-critical); }

.main { flex: 1; min-width: 0; }
.panel { padding: 20px 24px 22px; border-bottom: 1.5px solid var(--ink); }
.panel-focus { border-left: 4px solid var(--red); }
.panel-head { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; margin-bottom: 6px; flex-wrap: wrap; }
.panel-title { font-size: 18px; font-weight: 800; letter-spacing: -0.01em; margin: 0; }
.panel-meta { font-size: 10.5px; color: var(--muted); }
.panel-sub { font-size: 12px; color: var(--muted); margin: 0 0 16px; line-height: 1.5; }
.panel-note { font-size: 12px; color: var(--ink); margin: 12px 0 0; line-height: 1.55; }
.sub-head { display: flex; gap: 12px; align-items: baseline; margin: 18px 0 4px; font-size: 13px; flex-wrap: wrap; }
.panel-pair { display: grid; grid-template-columns: 1fr 1fr; }
.panel-pair > section.panel { border-right: 1px solid var(--hairline); }
.panel-pair > div > .panel { border-right: none; }
.table-foot { font-size: 11px; color: var(--muted); line-height: 1.6; margin: 14px 0 0; max-width: 860px; }

.footer-cols {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px;
  padding: 22px 24px 26px; border-bottom: 1px solid var(--hairline);
}
.foot-title {
  font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase;
  margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid var(--ink);
}
.footer-cols p { font-size: 11.5px; line-height: 1.6; color: var(--ink); margin: 0 0 10px; }
.footer-cols p b { font-weight: 700; }
.bottom-bar {
  display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 10px 24px 40px; font-size: 9.5px; letter-spacing: 0.12em; color: var(--muted);
}

/* ── mobile ────────────────────────────────────────────── */
@media (max-width: 960px) {
  .board-review { overflow-x: hidden; }
  .layout { flex-direction: column; }
  .layout, .main, .panel { max-width: 100%; }
  .main :deep(svg.chart), .main :deep(.chart-box) { max-width: 100%; }
  .panel-pair > * { min-width: 0; }
  .rail {
    position: static; width: auto; max-height: none; border-right: none;
    border-bottom: 1.5px solid var(--ink); padding-bottom: 0;
  }
  .rail-body { padding-bottom: 18px; }
  .stat-strip { grid-template-columns: repeat(2, 1fr); }
  .stat-strip > * { border-bottom: 1px solid var(--hairline); }
  .panel-pair { grid-template-columns: 1fr; }
  .panel-pair > section.panel { border-right: none; }
  .footer-cols { grid-template-columns: 1fr; gap: 18px; }
  .hero-figure { font-size: 52px; }
  .verdict { gap: 20px; padding: 22px 18px 18px; }
  .verdict-line { font-size: 15.5px; }
  .verdict-stats { gap: 20px; }
  .panel { padding: 18px 16px 20px; }
  .topbar { padding: 10px 16px; }
  .footer-cols { padding: 18px 16px 22px; }
  .bottom-bar { padding: 10px 16px 32px; }
}

/* ── print ─────────────────────────────────────────────── */
@media print {
  .no-print { display: none !important; }
  .rail { display: none !important; }
  .verdict { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .panel { break-inside: avoid; }
}
</style>
